// The one binding between a saved-location form and `AddressFormFields`
// (BL-2664): admin's `LocationFieldsSection` and bidder's `LocationFormModal`
// both render their address block through it, so the two can't drift on the
// country lock, the required marks or how an address change fills the form.
'use client';

import { useCallback, useMemo } from 'react';

import type { AddressErrors, AddressFormFieldsProps, AddressValue } from '../components/AddressFormFields';

/** The four address fields a location form stores flat beside its other fields. */
export type LocationAddressFieldName = 'street' | 'city' | 'state' | 'postalCode';

/** Locations are U.S.-only today, so the country is locked rather than stored. */
const LOCATION_COUNTRY = 'United States';

/** Input to {@link useLocationAddressFields}. */
export interface LocationAddressFieldsInput {
  /** The form's current values; a blank field may be absent. */
  values: Partial<Record<LocationAddressFieldName, string | undefined>>;
  /** Each field's inline error message, when it has one. */
  errors: Partial<Record<LocationAddressFieldName, string | undefined>>;
  /** Writes one field into the form. */
  onFieldChange: (field: LocationAddressFieldName, value: string) => void;
}

/** The `AddressFormFields` props the binding owns; the caller adds its dialect props. */
export type LocationAddressFieldsProps = Required<Pick<AddressFormFieldsProps, 'value' | 'onChange' | 'errors' | 'lockedCountry' | 'required'>>;

/**
 * Binds a location form's flat address values to `AddressFormFields`, locked to
 * the United States with Street, City and State marked required.
 *
 * @param input - The form's values, errors and field writer.
 * @returns Props to spread onto `AddressFormFields` (memoized while the fields are unchanged).
 */
export function useLocationAddressFields({ values, errors, onFieldChange }: LocationAddressFieldsInput): LocationAddressFieldsProps {
  // Stable references so AddressFormFields doesn't see a new object identity on every render.
  const value = useMemo<AddressValue>(
    () => ({
      street: values.street ?? '',
      city: values.city ?? '',
      state: values.state ?? '',
      postalCode: values.postalCode ?? '',
      country: LOCATION_COUNTRY,
    }),
    [values.street, values.city, values.state, values.postalCode],
  );
  const addressErrors = useMemo<AddressErrors>(
    () => ({ street: errors.street, city: errors.city, state: errors.state, postalCode: errors.postalCode }),
    [errors.street, errors.city, errors.state, errors.postalCode],
  );
  const onChange = useCallback(
    (next: AddressValue) => {
      onFieldChange('street', next.street);
      onFieldChange('city', next.city);
      onFieldChange('state', next.state);
      onFieldChange('postalCode', next.postalCode);
    },
    [onFieldChange],
  );

  return { value, onChange, errors: addressErrors, lockedCountry: LOCATION_COUNTRY, required: true };
}
